import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native'
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated'

import { RAMP_PROGRESS, hsbRamp } from '../utils/colorMotion'
import {
  CONTENT_FADE_DELAY,
  CONTENT_FADE_TIMING,
  MOTION_TIMING,
} from '../utils/motion'

export const DepositActionMotion = createContext(false)

interface ActionPhase {
  /** What the slot wears now; the next phase crosses from it. */
  fillColor: string | null
  /** What a provider's own control in the slot is painted, for when the slot's
   *  cover comes off and that control is what the user is looking at. */
  controlFill: string | null
}

const DepositActionPhase = createContext<ActionPhase | null>(null)

/**
 * The colour the deposit action is painted, carried across the components that
 * take turns filling its slot — a waiting state, a pill, a provider's own
 * button. Each new one picks the tween up where the last left off, so a phase
 * change reads as one control changing colour rather than a swap.
 */
export function DepositActionPhaseProvider({
  children,
}: {
  children: ReactNode
}) {
  const phase = useRef<ActionPhase>({ fillColor: null, controlFill: null })
  return (
    <DepositActionPhase.Provider value={phase.current}>
      {children}
    </DepositActionPhase.Provider>
  )
}

export function useDepositActionPhase(): ActionPhase | null {
  return useContext(DepositActionPhase)
}

/**
 * For a control that paints its own fill in the action's slot — a provider's
 * hosted button, which no layer of ours draws. Pass `null` where it isn't
 * standing in the slot.
 */
export function useDepositActionControlFill(fill: string | null) {
  const phase = useDepositActionPhase()
  useEffect(() => {
    if (phase === null) {
      return
    }
    phase.controlFill = fill
    return () => {
      phase.controlFill = null
    }
  }, [phase, fill])
}

export function DepositActionLayers({
  stateKey,
  fillColor,
  radius,
  borderCurve,
  backdropColor,
  fillOpacity = 1,
  style,
  contentStyle,
  contentOpacity = 1,
  children,
}: {
  /** Changes when the content does, replaying its fade. */
  stateKey: string
  /** What the fill wears in this phase; the tween runs to it. */
  fillColor: string
  radius: number
  borderCurve?: ViewStyle['borderCurve']
  /** Opaque backing, for a fill a dimmed state makes see-through. */
  backdropColor?: string
  fillOpacity?: number
  style?: StyleProp<ViewStyle>
  contentStyle?: StyleProp<ViewStyle>
  contentOpacity?: number
  children: ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const phase = useDepositActionPhase()
  // Read once: from mount on, this instance is what paints the slot.
  const [enterColor] = useState(() => phase?.fillColor ?? fillColor)
  const [entryRamp] = useState(() => hsbRamp(enterColor, fillColor))
  const crossing = useSharedValue(
    reduceMotion || enterColor === fillColor ? 1 : 0,
  )
  const ramp = useSharedValue(entryRamp)
  const painted = useRef(fillColor)
  const fade = useSharedValue(reduceMotion ? 1 : 0)

  useEffect(() => {
    if (phase !== null) {
      phase.fillColor = fillColor
    }
  }, [phase, fillColor])

  useEffect(() => {
    if (reduceMotion) {
      ramp.value = hsbRamp(fillColor, fillColor)
      painted.current = fillColor
      crossing.value = 1
      return
    }
    if (painted.current !== fillColor) {
      // Mid-crossing the fill wears neither end of the ramp, so the next one
      // starts from what is on screen.
      ramp.value = hsbRamp(
        interpolateColor(crossing.value, RAMP_PROGRESS, ramp.value),
        fillColor,
      )
      painted.current = fillColor
      crossing.value = 0
    }
    if (crossing.value < 1) {
      crossing.value = withTiming(1, MOTION_TIMING)
    }
  }, [fillColor, reduceMotion, ramp, crossing])

  useEffect(() => {
    if (reduceMotion) {
      fade.value = 1
      return
    }
    fade.value = 0
    fade.value = withDelay(
      CONTENT_FADE_DELAY,
      withTiming(1, CONTENT_FADE_TIMING),
    )
  }, [stateKey, reduceMotion, fade])

  const fillMotion = useAnimatedStyle(() => {
    'worklet'
    return {
      backgroundColor: interpolateColor(
        crossing.value,
        RAMP_PROGRESS,
        ramp.value,
      ),
    }
  })
  const contentMotion = useAnimatedStyle(() => {
    'worklet'
    return { opacity: fade.value * contentOpacity }
  })
  return (
    <View style={style}>
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFill,
          { borderRadius: radius, borderCurve, backgroundColor: backdropColor },
        ]}
      >
        <Animated.View
          style={[
            styles.fill,
            { borderRadius: radius, borderCurve, opacity: fillOpacity },
            fillMotion,
          ]}
        />
      </View>
      <Animated.View style={[contentStyle, contentMotion]}>
        {children}
      </Animated.View>
    </View>
  )
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
})
